import React, { useEffect, useMemo, useState } from "react";
import { invalidate } from "../../utils/swrCache";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  Select,
  Spinner,
  Subtitle1,
  Text,
} from "@fluentui/react-components";
import {
  type AppUser,
  listAppUsers,
  userDisplay,
} from "../../services/userService";
import {
  type MailboxAccessAdmin,
  type MailboxCalendarAccess,
  type MailboxMailAccess,
  type MailboxProbeResult,
  type MailboxType,
  mailboxAccessAdminService,
} from "../../services/mailboxAccessAdminService";
import { useBestaetigen } from "../../components/redesign/bestaetigen";

const CALENDAR_ACCESS_OPTIONS: Array<{ value: MailboxCalendarAccess; label: string }> = [
  { value: "NONE", label: "Kein Zugriff" },
  { value: "READ", label: "Lesen" },
  { value: "WRITE", label: "Schreiben" },
];

// B9neu (User-Entscheid): Mail-Zugriff ist jetzt in der UI pflegbar. Rang-Semantik
// des Backends: SEND beinhaltet Lesen. Default bei NEUEN Freigaben = SEND
// („Zugriff = lesen + senden"); „Nur lesen" ist die Ausnahme. Bestehende Zeilen
// werden NICHT still hochgestuft — Änderung nur explizit über diese Spalte.
const MAIL_ACCESS_OPTIONS: Array<{ value: MailboxMailAccess; label: string }> = [
  { value: "NONE", label: "Kein Zugriff" },
  { value: "READ", label: "Nur lesen" },
  { value: "SEND", label: "Lesen + Senden" },
  { value: "FULL", label: "Voll" },
];

const MAILBOX_TYPE_OPTIONS: Array<{ value: MailboxType; label: string }> = [
  { value: "user", label: "Benutzer" },
  { value: "room", label: "Raum" },
  { value: "shared", label: "Geteilt" },
];

const labelForUser = (
  userId: string,
  users: AppUser[],
): string => {
  const hit = users.find((u) => u.id === userId);
  return hit ? userDisplay(hit) : userId;
};

export const MailboxAccessSection: React.FC = () => {
  const { bestaetigen, hinweisen } = useBestaetigen();
  const [rows, setRows] = useState<MailboxAccessAdmin[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // New-assignment form state.
  const [newUserId, setNewUserId] = useState("");
  const [newMailbox, setNewMailbox] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newCalendarAccess, setNewCalendarAccess] = useState<MailboxCalendarAccess>("READ");
  // B9neu: Default = SEND (lesen + senden) — „nur lesen" ist die Ausnahme.
  const [newMailAccess, setNewMailAccess] = useState<MailboxMailAccess>("SEND");
  const [newCanCreateTeams, setNewCanCreateTeams] = useState(false);
  const [newMailboxType, setNewMailboxType] = useState<MailboxType>("user");
  const [creating, setCreating] = useState(false);
  // B9neu: Microsoft-Status je Postfach-Adresse (App-Erreichbarkeits-Probe).
  const [probes, setProbes] = useState<Record<string, MailboxProbeResult | "loading">>({});

  // Jede eindeutige Adresse einmal proben (bei neuen Zeilen automatisch nachziehen).
  useEffect(() => {
    const unique = Array.from(new Set(rows.map((r) => r.mailboxAddress.toLowerCase())));
    for (const mb of unique) {
      if (probes[mb] !== undefined) continue;
      setProbes((p) => ({ ...p, [mb]: "loading" }));
      void mailboxAccessAdminService
        .probe(mb)
        .then((res) => setProbes((p) => ({ ...p, [mb]: res })))
        .catch(() =>
          setProbes((p) => ({ ...p, [mb]: { reachable: false, reason: "Prüfung fehlgeschlagen" } })),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [accessRows, appUsers] = await Promise.all([
        mailboxAccessAdminService.list(),
        listAppUsers(),
      ]);
      setRows(accessRows);
      setUsers(appUsers);
      if (!newUserId && appUsers.length > 0) {
        setNewUserId(appUsers[0].id);
      }
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) =>
        a.mailboxAddress.localeCompare(b.mailboxAddress, "de"),
      ),
    [rows],
  );

  // B11: Nach jeder Freigaben-Mutation den Postfachlisten-Cache verwerfen —
  // der Key ist localStorage-persistiert und überlebte sonst sogar einen Reload,
  // sodass frisch verteilte Postfächer bis zu 5 min unsichtbar blieben.
  const invalidateMailboxCaches = () => invalidate("mail.mailboxes");

  const patchRow = async (
    id: string,
    patch: Parameters<typeof mailboxAccessAdminService.update>[1],
  ) => {
    setSavingId(id);
    try {
      const updated = await mailboxAccessAdminService.update(id, patch);
      invalidateMailboxCaches();
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (e: any) {
      void hinweisen({
        titel: "Speichern fehlgeschlagen",
        text: e?.message || "Die Änderung an der Freigabe konnte nicht gespeichert werden.",
        ton: "danger",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (row: MailboxAccessAdmin) => {
    const ok = await bestaetigen({
      titel: "Freigabe löschen?",
      text: `${labelForUser(row.userId, users)} verliert den Zugriff auf ${row.mailboxAddress}.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    setSavingId(row.id);
    try {
      await mailboxAccessAdminService.remove(row.id);
      invalidateMailboxCaches();
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e: any) {
      void hinweisen({
        titel: "Löschen fehlgeschlagen",
        text: e?.message || "Die Freigabe konnte nicht gelöscht werden.",
        ton: "danger",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    const mailbox = newMailbox.trim().toLowerCase();
    if (!newUserId || !mailbox) return;
    setCreating(true);
    try {
      const created = await mailboxAccessAdminService.create({
        userId: newUserId,
        mailboxAddress: mailbox,
        // B9neu: Mail-Zugriff explizit mitsenden (Default SEND = lesen + senden);
        // vorher fehlte das Feld → Backend-Default READ, nicht änderbar über die UI.
        mailAccess: newMailAccess,
        calendarAccess: newCalendarAccess,
        canCreateTeamsMeetings: newCanCreateTeams,
        mailboxType: newMailboxType,
        displayName: newDisplayName.trim() || null,
      });
      invalidateMailboxCaches();
      setRows((prev) => [...prev, created]);
      setNewMailbox("");
      setNewDisplayName("");
      setNewCalendarAccess("READ");
      setNewCanCreateTeams(false);
      setNewMailboxType("user");
    } catch (e: any) {
      void hinweisen({
        titel: "Anlegen fehlgeschlagen",
        text: e?.message || "Die Freigabe konnte nicht angelegt werden.",
        ton: "danger",
      });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Card style={{ padding: 20 }}>
        <Spinner label="Lade Postfach-Freigaben..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20 }}>
        <Text style={{ color: "#a00" }}>{error}</Text>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Subtitle1>Kalender-/Postfach-Freigaben</Subtitle1>
        <Text size={200} style={{ display: "block", color: "#666", marginTop: 4 }}>
          Verwaltet, welche Benutzer auf welche Postfächer/Kalender zugreifen dürfen.
        </Text>
      </div>

      <section>
        <Text weight="semibold" size={400}>Neue Freigabe anlegen</Text>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginTop: 8,
            alignItems: "end",
          }}
        >
          <Field label="Benutzer">
            <Select value={newUserId} onChange={(_, d) => setNewUserId(d.value)}>
              {users.length === 0 && <option value="">— keine Benutzer —</option>}
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {userDisplay(u)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Mailadresse (Postfach)">
            <Input
              type="email"
              value={newMailbox}
              placeholder="postfach@example.com"
              onChange={(_, d) => setNewMailbox(d.value)}
            />
          </Field>
          <Field label="Anzeigename">
            <Input
              value={newDisplayName}
              placeholder="Optional"
              onChange={(_, d) => setNewDisplayName(d.value)}
            />
          </Field>
          <Field label="Postfachtyp">
            <Select
              value={newMailboxType}
              onChange={(_, d) => setNewMailboxType(d.value as MailboxType)}
            >
              {MAILBOX_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Mail-Zugriff">
            <Select
              value={newMailAccess}
              onChange={(_, d) => setNewMailAccess(d.value as MailboxMailAccess)}
            >
              {MAIL_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kalenderzugriff">
            <Select
              value={newCalendarAccess}
              onChange={(_, d) => setNewCalendarAccess(d.value as MailboxCalendarAccess)}
            >
              {CALENDAR_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Teams-Besprechungen">
            <Checkbox
              checked={newCanCreateTeams}
              label="erlauben"
              onChange={(_, d) => setNewCanCreateTeams(Boolean(d.checked))}
            />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button
            appearance="primary"
            disabled={creating || !newUserId || newMailbox.trim().length === 0}
            onClick={handleCreate}
          >
            {creating ? <Spinner size="tiny" /> : "Freigabe anlegen"}
          </Button>
        </div>
      </section>

      <section>
        <Text weight="semibold" size={400}>Bestehende Freigaben ({sortedRows.length})</Text>
        <Text size={200} style={{ display: "block", color: "#666", marginBottom: 8 }}>
          Änderungen werden beim Umschalten direkt gespeichert.
        </Text>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                <th style={{ padding: 8 }}>Benutzer</th>
                <th style={{ padding: 8 }}>Postfach</th>
                <th style={{ padding: 8 }}>Microsoft</th>
                <th style={{ padding: 8 }}>Anzeigename</th>
                <th style={{ padding: 8 }}>Postfachtyp</th>
                <th style={{ padding: 8 }}>Mail-Zugriff</th>
                <th style={{ padding: 8 }}>Kalenderzugriff</th>
                <th style={{ padding: 8 }}>Teams</th>
                <th style={{ padding: 8, textAlign: "right" }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 12, color: "#666" }}>
                    Keine Freigaben vorhanden.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => {
                  const busy = savingId === row.id;
                  return (
                    <tr key={row.id} style={{ borderBottom: "1px solid #f1f1f1" }}>
                      <td style={{ padding: 8 }}>{labelForUser(row.userId, users)}</td>
                      <td style={{ padding: 8 }}>{row.mailboxAddress}</td>
                      <td style={{ padding: 8 }}>
                        <MicrosoftStatus probe={probes[row.mailboxAddress.toLowerCase()]} />
                      </td>
                      <td style={{ padding: 8 }}>
                        <InlineDisplayName
                          value={row.displayName}
                          disabled={busy}
                          onSave={(value) =>
                            patchRow(row.id, { displayName: value })
                          }
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={row.mailboxType}
                          disabled={busy}
                          onChange={(_, d) =>
                            patchRow(row.id, { mailboxType: d.value as MailboxType })
                          }
                        >
                          {MAILBOX_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={row.mailAccess}
                          disabled={busy}
                          onChange={(_, d) =>
                            patchRow(row.id, { mailAccess: d.value as MailboxMailAccess })
                          }
                        >
                          {MAIL_ACCESS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={row.calendarAccess}
                          disabled={busy}
                          onChange={(_, d) =>
                            patchRow(row.id, {
                              calendarAccess: d.value as MailboxCalendarAccess,
                            })
                          }
                        >
                          {CALENDAR_ACCESS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: 8 }}>
                        <Checkbox
                          checked={row.canCreateTeamsMeetings}
                          disabled={busy}
                          onChange={(_, d) =>
                            patchRow(row.id, {
                              canCreateTeamsMeetings: Boolean(d.checked),
                            })
                          }
                        />
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        <Button
                          size="small"
                          appearance="subtle"
                          disabled={busy}
                          onClick={() => handleRemove(row)}
                        >
                          {busy ? <Spinner size="tiny" /> : "Löschen"}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Card>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <Label>{label}</Label>
    {children}
  </div>
);

/**
 * B9neu — „Microsoft-Status": zeigt, ob die APP das Postfach bei Microsoft
 * erreicht (Tippfehler/Lizenz/Access-Policy). Grund per Tooltip.
 */
const MicrosoftStatus: React.FC<{ probe?: MailboxProbeResult | "loading" }> = ({ probe }) => {
  if (!probe || probe === "loading") {
    return <Spinner size="extra-tiny" aria-label="Microsoft-Status wird geprüft" />;
  }
  if (probe.reachable) {
    return (
      <span style={{ color: "#107c10", fontWeight: 600 }} title="Postfach für die App erreichbar">
        ✓ erreichbar
      </span>
    );
  }
  return (
    <span style={{ color: "#a4262c", fontWeight: 600 }} title={probe.reason ?? "nicht erreichbar"}>
      ✗ nicht erreichbar
    </span>
  );
};

/**
 * Inline-editable display name cell. Shows the current value; on focus it can
 * be edited and persists on blur/Enter only when the value actually changed.
 */
const InlineDisplayName: React.FC<{
  value: string | null;
  disabled: boolean;
  onSave: (value: string | null) => void;
}> = ({ value, disabled, onSave }) => {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = () => {
    const next = draft.trim();
    const current = value ?? "";
    if (next === current) return;
    onSave(next === "" ? null : next);
  };

  return (
    <Input
      value={draft}
      disabled={disabled}
      placeholder="—"
      onChange={(_, d) => setDraft(d.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
    />
  );
};

export default MailboxAccessSection;
